import { useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from '@tanstack/react-router';
import { FilterableSelect } from '../common/FilterableSelect';
import { useProject, type OutlineSummaryDto } from '../../api/queries/projects';
import { GenerateOutlineDialog } from './GenerateOutlineDialog';

interface VersionPickerProps {
  projectId: string;
  currentOutlineId: string;
}

export function VersionPicker({
  projectId,
  currentOutlineId,
}: VersionPickerProps) {
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const [generateOpen, setGenerateOpen] = useState(false);

  const outlines = project?.outlines ?? [];

  const options = useMemo(
    () =>
      outlines.map((o: OutlineSummaryDto) => ({
        value: o.id,
        label: `${o.isPrimary ? '\u2605 ' : ''}v${o.versionNumber} \u2014 ${o.label || 'Untitled'}`,
      })),
    [outlines],
  );

  const currentOutline = outlines.find(
    (o: OutlineSummaryDto) => o.id === currentOutlineId,
  );

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: '/projects/$projectId', params: { projectId } })
          }
        >
          Project
        </Button>

        <Box sx={{ minWidth: 280 }}>
          <FilterableSelect
            label="Version"
            size="small"
            value={currentOutlineId}
            onChange={(newValue) => {
              if (newValue && newValue !== currentOutlineId) {
                navigate({
                  to: '/outlines/$outlineId',
                  params: { outlineId: newValue },
                });
              }
            }}
            options={options}
            allowEmpty={false}
          />
        </Box>

        {currentOutline?.sourceModel && (
          <Chip
            size="small"
            label={`AI (${currentOutline.sourceModel})`}
            variant="outlined"
          />
        )}

        {currentOutline?.stage && (
          <Chip size="small" label={currentOutline.stage} variant="outlined" />
        )}

        {currentOutline && (
          <Typography variant="caption" color="text.secondary">
            {currentOutline.nodeCount} node
            {currentOutline.nodeCount === 1 ? '' : 's'}
          </Typography>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<SmartToyIcon />}
          onClick={() => setGenerateOpen(true)}
        >
          Generate New
        </Button>
      </Stack>

      <GenerateOutlineDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        projectId={projectId}
      />
    </>
  );
}
